---
title: "Lista kontrolna BHP na budowie — co sprawdzić podczas audytu"
description: "Lista kontrolna BHP na budowie — kompletna checklista dla audytorów bezpieczeństwa: dokumentacja, prace na wysokości, rusztowania, wykopy, maszyny, ŚOI i elektryka."
date: "2026-06-07"
slug: "lista-kontrolna-bhp-na-budowie"
faq:
  - q: "Jak często przeprowadzać audyt BHP na budowie?"
    a: "Na aktywnych placach budowy zaleca się audyty co najmniej raz w tygodniu lub co dwa tygodnie. Dodatkowe audyty należy przeprowadzać po każdym wypadku, zdarzeniu potencjalnie wypadkowym lub zmianie zakresu robót."
  - q: "Kto może przeprowadzać audyt BHP?"
    a: "Audyt może przeprowadzić specjalista ds. BHP, inspektor pracy, koordynator BIOZ lub inna wyznaczona osoba z odpowiednią wiedzą. Nie ma ustawowego wymogu posiadania konkretnych uprawnień dla wewnętrznych audytów — liczy się kompetencja i znajomość przepisów."
  - q: "Co zrobić, jeśli podczas audytu stwierdzono krytyczne naruszenie?"
    a: "Krytyczne naruszenie (np. brak zabezpieczeń przy pracy na wysokości) wymaga natychmiastowego wstrzymania robót w zagrożonym miejscu i usunięcia niezgodności przed ich wznowieniem. Fakt stwierdzenia i podjętych działań należy odnotować w protokole."
  - q: "Jak długo przechowywać protokoły audytów BHP?"
    a: "Branżową praktyką jest min. 3 lata — przez cały czas trwania budowy i przez rok po jej zakończeniu. W przypadku wypadku przy pracy dokumenty mogą być potrzebne do postępowań, które trwają znacznie dłużej."
  - q: "Czy Pinveo zastępuje specjalistę BHP?"
    a: "Nie. Pinveo to narzędzie do dokumentacji — wspiera i przyspiesza pracę specjalisty BHP, ale nie zastępuje jego wiedzy ani uprawnień. Ocenę ryzyka i kwalifikację niezgodności przeprowadza człowiek."
---

Lista kontrolna BHP na budowie to podstawowe narzędzie każdego audytora bezpieczeństwa i koordynatora robót. Systematyczne audyty BHP zmniejszają ryzyko wypadków, dokumentują stan placu budowy i chronią inwestora przed odpowiedzialnością. Poniżej znajdziesz kompletną checklistę i wskazówki dotyczące dokumentowania niezgodności.

---

## Po co przeprowadza się audyty BHP na budowie?

Audyt BHP na budowie ma trzy główne cele:

1. **Prewencja wypadków** — identyfikacja zagrożeń zanim doprowadzą do zdarzenia.
2. **Zgodność z przepisami** — weryfikacja spełnienia wymagań rozporządzenia w sprawie bhp przy robotach budowlanych (Dz.U. 2003 nr 47 poz. 401).
3. **Dokumentacja prawna** — w razie wypadku audyty potwierdzają, że inwestor/generalny wykonawca sprawował nadzór.

Audyty powinny być przeprowadzane **regularnie** — tygodniowo lub co dwa tygodnie na aktywnych placach budowy — oraz po każdym zdarzeniu wypadkowym lub potencjalnie wypadkowym.

---

## Kompletna lista kontrolna BHP na budowie

### 1. Dokumentacja i organizacja placu budowy

- [ ] Aktualne pozwolenie na budowę dostępne na budowie.
- [ ] Plan BIOZ (Bezpieczeństwo i Ochrona Zdrowia) opracowany i zaktualizowany.
- [ ] Koordynator ds. BHP wyznaczony (jeśli wymagany — roboty powyżej 30 dni z >20 robotnikami).
- [ ] Tablica informacyjna z danymi kierownika budowy i numerami alarmowymi.
- [ ] Dziennik budowy prowadzony na bieżąco.
- [ ] Aktualne szkolenia BHP wszystkich pracowników (karty szkoleń dostępne).
- [ ] Badania lekarskie pracowników aktualne.

### 2. Teren budowy i strefy niebezpieczne

- [ ] Plac budowy ogrodzony i zabezpieczony przed dostępem osób nieupoważnionych.
- [ ] Tablice ostrzegawcze i zakazu wstępu umieszczone w widocznych miejscach.
- [ ] Drogi komunikacyjne na placu — stabilne, o odpowiedniej szerokości, bez przeszkód.
- [ ] Strefy niebezpieczne (pod rusztowaniami, przy urządzeniach dźwigowych) oznakowane.
- [ ] Oświetlenie placu budowy zapewniające bezpieczną pracę (min. 50 lux na stanowiskach pracy).
- [ ] Odpowiednie oznakowanie i zabezpieczenie mediów (kable, rury) w obszarach robót.

### 3. Prace na wysokości (powyżej 1 m)

- [ ] Wszystkie miejsca pracy na wysokości zabezpieczone — balustrady lub siatki ochronne.
- [ ] Szelki bezpieczeństwa używane przy pracach bez stałego zabezpieczenia zbiorowego.
- [ ] Drabiny w dobrym stanie technicznym, właściwie ustawione (kąt 75°, zabezpieczone u góry).
- [ ] Rusztowania posiadają tabliczkę dopuszczenia z datą odbioru i nośnością.
- [ ] Pomosty robocze z desek o grubości min. 38 mm, bez przerw powyżej 25 mm.
- [ ] Krawężniki przy krawędziach pomostów (min. 15 cm).

### 4. Rusztowania

- [ ] Rusztowanie montowane/demontowane przez osoby z uprawnieniami.
- [ ] Protokół odbioru rusztowania dostępny na budowie.
- [ ] Kotwy i zakotwienia rusztowania w stanie nienaruszonym.
- [ ] Siatki ochronne lub deski krawężnikowe na wszystkich poziomach.
- [ ] Wejścia na rusztowanie prawidłowe (drabiny wewnętrzne lub schody).
- [ ] Obciążenie rusztowania nie przekracza dopuszczalnego (tabliczka z nośnością).

### 5. Wykopy i roboty ziemne

- [ ] Ściany wykopu zabezpieczone — szalowanie lub skarpy o dopuszczalnym kącie.
- [ ] Krawędzie wykopu zabezpieczone barierkami (min. 1 m od krawędzi).
- [ ] Zejścia do wykopów zapewnione (drabiny lub rampy) — co max. 20 m.
- [ ] Teren przy wykopie wolny od nadkładu i materiałów (min. 0,6 m od krawędzi).
- [ ] Sprawdzenie obecności instalacji podziemnych przed rozpoczęciem robót.
- [ ] Monitoring głębokich wykopów w gruntach niestabilnych.

### 6. Maszyny i urządzenia budowlane

- [ ] Maszyny posiadają aktualną dokumentację techniczną (DTR) i deklaracje zgodności.
- [ ] Operatorzy posiadają wymagane uprawnienia (operator maszyn roboczych — UDT lub inne).
- [ ] Strefy pracy maszyn wyznaczone i oznakowane.
- [ ] Urządzenia dźwignicze (żurawie, podnosniki) z aktualnymi protokołami UDT.
- [ ] Codzienna kontrola stanu technicznego maszyn przez operatora (dziennik maszyny).
- [ ] Maszyny nieużywane zabezpieczone przed dostępem osób nieupoważnionych.

### 7. Środki ochrony indywidualnej (ŚOI)

- [ ] Wszyscy pracownicy wyposażeni w kaski ochronne (na całym placu budowy).
- [ ] Obuwie ochronne z podnoskiem stalowym używane przez wszystkich.
- [ ] Kamizelki odblaskowe — pracownicy widoczni dla operatorów maszyn.
- [ ] Rękawice ochronne dostępne przy pracach narażających ręce.
- [ ] Ochrona słuchu przy hałaśliwych pracach (powyżej 85 dB).
- [ ] Maski i filtry przy pracach zapylonych (cięcie, szlifowanie) lub chemicznych.
- [ ] ŚOI w dobrym stanie technicznym, bez uszkodzeń, z datami ważności.

### 8. Instalacje elektryczne tymczasowe

- [ ] Rozdzielnice tymczasowe z ochroną przeciwporażeniową (RCD/GFCI 30 mA).
- [ ] Kable elektryczne chronione przed uszkodzeniem mechanicznym (rury, kanały).
- [ ] Brak połączeń prowizorycznych i improwizowanych.
- [ ] Tablice rozdzielcze zamknięte na klucz lub zabezpieczone.
- [ ] Agregaty prądotwórcze właściwie uziemione.
- [ ] Przegląd instalacji tymczasowej co 6 miesięcy lub po każdej przebudowie.

### 9. Prace spawalnicze i gorące

- [ ] Pozwolenie na prace gorące wystawione przed rozpoczęciem.
- [ ] Gaśnica w pobliżu miejsca prac gorących.
- [ ] Materiały palne odsunięte lub zabezpieczone w promieniu co najmniej 5 m.
- [ ] Spawacz w pełnym ŚOI (maska spawalnicza, rękawice, fartuch skórzany).
- [ ] Wentylacja pomieszczenia przy spawaniu w zamkniętej przestrzeni.

### 10. Pierwsza pomoc i procedury awaryjne

- [ ] Apteczka pierwszej pomocy dostępna i kompletna.
- [ ] Numery alarmowe (112, 999, 998, PIP) wywieszone w widocznym miejscu.
- [ ] Drogi ewakuacyjne wolne i oznakowane.
- [ ] Co najmniej jedna osoba na budowie z aktualnym szkoleniem z pierwszej pomocy.
- [ ] Procedura postępowania przy wypadku opisana w planie BIOZ.

---

## Regularne audyty BHP — jak je organizować?

Skuteczny program audytów BHP wymaga:

- **Harmonogramu** — stałe terminy (np. każdy piątek lub pierwszy poniedziałek miesiąca).
- **Przypisania odpowiedzialności** — konkretna osoba odpowiedzialna za przeprowadzenie i dokumentację.
- **Standaryzowanej listy kontrolnej** — jednolity formularz dla wszystkich audytorów.
- **Systemu follow-up** — każda niezgodność ma przypisaną osobę odpowiedzialną i termin usunięcia.
- **Archiwizacji** — protokoły audytów przechowywane przez min. 3 lata.

---

## Dokumentowanie niezgodności — najlepsze praktyki

Każda niezgodność stwierdzona podczas audytu powinna być udokumentowana z:

- **lokalizacją** na planie placu budowy lub zdjęciem z GPS,
- **opisem wady** (co, gdzie, dlaczego to niezgodność),
- **oceną ryzyka** (niskie / średnie / wysokie / krytyczne — wymaga natychmiastowego działania),
- **przypisaną odpowiedzialnością** (kto ma usunąć i do kiedy),
- **fotografią przed i po usunięciu niezgodności**.

Bez rzetelnej dokumentacji trudno udowodnić, że audyt w ogóle się odbył — a w przypadku kontroli PIP lub wypadku to dokumentacja decyduje o odpowiedzialności.

---

## Jak Pinveo wspiera audyty BHP?

Pinveo przyspiesza każdy etap audytu BHP na budowie:

- **Formularze BHP** — gotowe szablony z checklistą dostosowaną do placu budowy.
- **Markery na rzucie PDF** — oznaczanie niezgodności bezpośrednio na planie placu budowy lub kondygnacji.
- **Zdjęcia z GPS i znacznikiem czasu** — dowód lokalizacji i momentu stwierdzenia usterki.
- **Śledzenie statusu niezgodności** — przypisanie do osoby odpowiedzialnej, termin, weryfikacja po usunięciu.
- **Raport PDF i Excel** — gotowy protokół audytu do przekazania kierownikowi budowy lub inwestorowi.
- **Praca offline** — dokumentowanie bez zasięgu na terenie budowy.

Więcej o zastosowaniu Pinveo przy audytach BHP: [Audyty BHP z Pinveo](/zastosowania/bhp-audyty).

---

## Podsumowanie

Lista kontrolna BHP na budowie obejmuje dziesięć kluczowych obszarów — od dokumentacji i organizacji placu, przez prace na wysokości i rusztowania, po instalacje elektryczne i procedury awaryjne. Regularne, udokumentowane audyty to podstawa zarządzania bezpieczeństwem i ochrony prawnej. Pinveo umożliwia dokumentowanie niezgodności z lokalizacją na rzucie, zdjęciami z GPS i gotowym raportem PDF.

---

## FAQ

### Jak często przeprowadzać audyt BHP na budowie?

Na aktywnych placach budowy zaleca się audyty co najmniej raz w tygodniu lub co dwa tygodnie. Dodatkowe audyty należy przeprowadzać po każdym wypadku, zdarzeniu potencjalnie wypadkowym lub zmianie zakresu robót.

### Kto może przeprowadzać audyt BHP?

Audyt może przeprowadzić specjalista ds. BHP, inspektor pracy, koordynator BIOZ lub inna wyznaczona osoba z odpowiednią wiedzą. Nie ma ustawowego wymogu posiadania konkretnych uprawnień dla wewnętrznych audytów — liczy się kompetencja i znajomość przepisów.

### Co zrobić, jeśli podczas audytu stwierdzono krytyczne naruszenie?

Krytyczne naruszenie (np. brak zabezpieczeń przy pracy na wysokości) wymaga natychmiastowego wstrzymania robót w zagrożonym miejscu i usunięcia niezgodności przed ich wznowieniem. Fakt stwierdzenia i podjętych działań należy odnotować w protokole.

### Jak długo przechowywać protokoły audytów BHP?

Przepisy Kodeksu pracy i rozporządzeń BHP nie wskazują wprost minimalnego okresu. Branżową praktyką jest min. 3 lata — przez cały czas trwania budowy i przez rok po jej zakończeniu. W przypadku wypadku przy pracy dokumenty mogą być potrzebne do postępowań, które trwają znacznie dłużej.

### Czy Pinveo zastępuje specjalistę BHP?

Nie. Pinveo to narzędzie do dokumentacji — wspiera i przyspiesza pracę specjalisty BHP, ale nie zastępuje jego wiedzy ani uprawnień. Ocenę ryzyka i kwalifikację niezgodności przeprowadza człowiek.
